import '@assets/styles/pages/product-recommendations.scss';
import { Stepper } from '@components/common';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { ProductDiscussion } from './productDiscussion';
import { FeedbackExpectation } from './feedbackExpectation';
import { useFormik } from 'formik';
import { DEFAULT_PRODUCT_RECOMMENDATION, defaultTaskListFormInitialValues, IFeedback } from '.';
import { useSiteLeadStore } from '@store/siteLeadStore';
import { useTaskListProductsQuery } from '@services/hooks/site-leads/useTaskListProductsQuery';
import useStepperStore from '@store/stepperStore';
import { SiteLeadData } from '@services/hooks/site-leads';
import { useValidation } from '@hooks/validations';
import { useLocation } from 'react-router-dom';
import { useVisitFeedbackMutation } from '@services/hooks/site-leads/useVisitFeedbackMutation';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { isNullOrEmpty } from '@components/delaerVisit/components/addEditDealerVisit';
import { ToastType, useTosterStore } from '@store/toster';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '@services/hooks/profile';
import { RoleType } from '@helper/utils';
import { QR_KEYS, SUPERVISOR_ALLOWED_CLASS, VISIT_STATUS } from '@config/constant';
import { VisitCompleteQr } from '../visitCompleteQr/visitCompleteQr';
import { useUpdateSiteVisitStatusMutation } from '@services/hooks/site-leads/useUpdateSiteVisitStatus';
import { Modal } from '@components/common/modal/modal';
import { overlayRemoveFadeAction } from '@components/common/sort';
import { useQrVerificationStore } from '@store/qrVerificationStore';
import { useGeoLocationDetails } from '@hooks/useGeoLocationDetails';
import { bindSiteVisitRecordingSiteId } from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { useShowPauseReminderOnVisitFormEdit } from '@components/siteLeads/siteVisitRecording/useShowPauseReminderOnVisitFormEdit';
import { useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';

export const TaskList = (): ReactElement => {
	useShowPauseReminderOnVisitFormEdit();
	useSetMainHeader('Task List');
	const { state } = useLocation();
	const [totalSteps, setTotalSteps] = useState<number>(3);
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const role = profile[0]?.usertype_c;
	const {
		siteName,
		siteLocation,
		glue,
		type_of_contract,
		reason,
		grade,
		setSiteVisitId,
		setSiteLeadId,
		brandUsed,
		substrates,
		siteDetails,
		proposed_field_competition_sites,
		builder_name,
		builder_phone_number,
		builder_office_address,
	} = useSiteLeadStore();

	const membershipNumber =
		siteDetails?.membershipno === '' ? siteDetails?.lead_membership_no : siteDetails?.membershipno;
	const {
		data: taskListProducts,
		isFetching: isTaskListProductsFetching,
		error: taskListProductsError,
	} = useTaskListProductsQuery(membershipNumber);
	const { setCurrentStep } = useStepperStore();
	const { TaskListValidationSchema } = useValidation();
	const { mutate: visitFeedbackMutation, isPending: isVisitFeedbackPending } = useVisitFeedbackMutation();
	const displayProducts = useMemo(() => {
		const apiProducts = taskListProducts?.data?.product_recommendations ?? [];
		return [...apiProducts, DEFAULT_PRODUCT_RECOMMENDATION];
	}, [taskListProducts]);

	const listProducts = taskListProducts?.data?.product_recommendations;
	const { setToaster } = useTosterStore();
	const { mutate: updateSiteVisitStatus } = useUpdateSiteVisitStatusMutation();
	const [verifyQrModal, setVerifyQrModal] = useState<boolean>(false);
	const isSiteQrVerified = useQrVerificationStore((s) => s.isVerified(QR_KEYS.SITE));
	const { locationDetails } = useGeoLocationDetails();

	const formik = useFormik({
		initialValues: defaultTaskListFormInitialValues,
		onSubmit: () => {},
		validationSchema: TaskListValidationSchema,
	});

	const commonProps = {
		formik,
	};

	const returnFeedbackResponseValue = (product: IFeedback): string => {
		//TODO: Need to refactor this condition
		const response = product?.feedbackResponse;
		if (typeof response === 'boolean' && response) {
			return 'True';
		}
		if (response && typeof response === 'string' && response.length > 0) {
			return response;
		}
		return 'False';
	};

	const siteLeadPayload = useCallback((): SiteLeadData => {
		const { feedback, exception } = formik.values;

		const payload: SiteLeadData = {
			site_lead: {
				name: siteName,
				site_location_c: siteLocation,
				end_user_c: siteDetails?.sfid,
				glue_used_at_the_site: glue,
				grade_glue_used_at_the_site: grade,
				reason_of_competition: glue === 'Competition' ? reason : null,
				type_of_contract: type_of_contract,
				brand_of_glue_used_at_site: glue === 'Competition' ? brandUsed : '',
				market: 'BV',
				contract_phone: siteDetails?.name,
				contract_name: siteDetails?.number,
				roff_substrate_c: isNullOrEmpty(substrates),
				proposed_field_competition_sites: proposed_field_competition_sites,
				builder_name: builder_name,
				builder_phone_number: builder_phone_number,
				builder_office_address: builder_office_address,
			},
			visit: {
				product1_demoed_code: listProducts?.[0]?.product_to_be ? null : listProducts?.[0]?.product_code,
				product1_demoed_name: listProducts?.[0]?.product_to_be ? null : listProducts?.[0]?.product_name,
				product2_demoed_code: listProducts?.[1]?.product_to_be ? null : listProducts?.[1]?.product_code,
				product2_demoed_name: listProducts?.[1]?.product_to_be ? null : listProducts?.[1]?.product_name,
				product1_discussed: listProducts?.[0]?.product_to_be
					? `${listProducts?.[0]?.product_code}-${listProducts?.[0]?.product_name}`
					: null,
				product2_discussed: listProducts?.[1]?.product_to_be
					? `${listProducts?.[1]?.product_code}-${listProducts?.[1]?.product_name}`
					: null,
			},
			product_feedback: [],
			product_answered: [],
			slab_answered: true,
			feedback: {
				bde_feedback: formik.values.productFeedback || '',
				any_feedback: formik.values.anyFeedback || '',
			},
		};

		if (state?.siteId) {
			payload.site_lead = {
				site_id: state?.siteId,
			};
		}
		if (feedback.length > 0) {
			payload.product_answered = formik.values.productResponse.map((product, index) => ({
				product: listProducts?.[index].product_code,
				answer: product,
			}));

			payload.product_feedback = feedback
				.flat()
				.filter((product): product is IFeedback => !!product && product.feedbackQuestion != null)
				.map((product) => ({
					question_type: 'product_feedback',
					question: product.feedbackQuestion,
					answer: returnFeedbackResponseValue(product),
					product: product.productId,
				}));
		}

		// Add feedback object with bde_feedback and any_feedback
		if (formik.values.productFeedback || formik.values.anyFeedback) {
			payload.feedback = {
				bde_feedback: formik.values.productFeedback || '',
				any_feedback: formik.values.anyFeedback || '',
			};
		}

		if (exception.length > 0) {
			payload.product_feedback = [
				...payload.product_feedback,
				...exception
					.filter((elem): elem is IFeedback => !!elem && elem.feedbackQuestion != null)
					.map((elem) => ({
						question_type: 'visit_feedback',
						question: elem.feedbackQuestion,
						answer: typeof elem.feedbackResponse === 'string' ? elem.feedbackResponse : '',
					})),
			];
		}

		return payload;
	}, [
		formik.values,
		siteName,
		siteLocation,
		siteDetails,
		glue,
		grade,
		reason,
		type_of_contract,
		brandUsed,
		substrates,
		listProducts,
		state?.siteId,
	]);

	const handleNext = useCallback((): void => {
		const payload = siteLeadPayload();

		visitFeedbackMutation(payload, {
			onSuccess: (data) => {
				if (data && Object.keys(data).length) {
					const visitId = data?.visit?.id;
					const createdSiteId = data?.site_lead?.id ?? state?.siteId;
					if (visitId && isSiteQrVerified) {
						updateSiteVisitStatus(
							{
								siteVisitId: String(visitId),
								status: VISIT_STATUS.VERIFIED,
								contractor_latitude_c: locationDetails?.latitude ?? null,
								contractor_longitude_c: locationDetails?.longitude ?? null,
							},
							{
								onSuccess: () => setToaster(true, ToastType.success, 'Visit verified successfully.'),
								onError: (e) => setToaster(true, ToastType.error, e?.message ?? 'Failed to update status.'),
							}
						);
					}
					setSiteVisitId(visitId);
					setSiteLeadId(createdSiteId);
					if (createdSiteId != null) {
						void bindSiteVisitRecordingSiteId(createdSiteId);
					}
					setCurrentStep(2);
				}
			},
			onError: (error) => setToaster(true, ToastType.error, error?.message),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		siteLeadPayload,
		visitFeedbackMutation,
		setSiteVisitId,
		setSiteLeadId,
		state?.siteId,
		setCurrentStep,
		isSiteQrVerified,
		updateSiteVisitStatus,
		setToaster,
	]);

	const handleVerifyModalClose = useCallback((): void => {
		setVerifyQrModal(false);
		overlayRemoveFadeAction(() => {});
	}, []);

	const handleVerifyModalOpen = useCallback((): void => {
		if (isSiteQrVerified) {
			setToaster(true, ToastType.success, 'Visit is already verified.');
			return;
		}
		const recordingStore = useSiteVisitRecordingStore.getState();
		if (recordingStore.phase === 'paused') {
			recordingStore.notifyPausedFormEdit();
			return;
		}
		setVerifyQrModal(true);
	}, [isSiteQrVerified, setToaster]);

	const isNextDisabled = useMemo(() => {
		const hasAllProductResponses =
			formik.values.productResponse.filter((item) => item !== null).length === listProducts?.length;

		const hasBdeFeedback = formik.values.productFeedback && formik.values.productFeedback.trim().length >= 2;

		const hasAnyFeedback = formik.values.anyFeedback && formik.values.anyFeedback.trim().length >= 2;

		const result = !hasAllProductResponses || !hasBdeFeedback || !hasAnyFeedback || !formik.isValid || !formik.dirty;

		return result;
	}, [
		formik.values.productResponse,
		formik.values.productFeedback,
		formik.values.anyFeedback,
		listProducts?.length,
		formik.isValid,
		formik.dirty,
	]);

	<Stepper
		nextHandler={handleNext}
		isPending={isVisitFeedbackPending}
		isNext={isNextDisabled}
		totalSteps={totalSteps}
	/>;

	useEffect(() => {
		if (role === RoleType.KAE && SUPERVISOR_ALLOWED_CLASS.includes(siteDetails?.contractorclass || '')) {
			setTotalSteps(4);
		} else {
			setTotalSteps(3);
		}
	}, [siteDetails, role]);

	// Ensure form recognizes the new feedback fields
	useEffect(() => {
		// Mark form as dirty if feedback fields have values
		if (formik.values.productFeedback || formik.values.anyFeedback) {
			formik.setFieldTouched('productFeedback', true, false);
			formik.setFieldTouched('anyFeedback', true, false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.productFeedback, formik.values.anyFeedback]);

	return (
		<section className="task-list-section">
			<div className="container">
				<Wrapper isLoaderVisible={isTaskListProductsFetching} isError={taskListProductsError}>
					<>
						<ProductDiscussion products={displayProducts} formik={formik} />
						{/* <SchemeDiscussion {...commonProps} slabData={currentSlab} fpcDetails={fpcDetails} /> */}

						<div className="product-feedback-wrapper">
							<h2 className="list-title">
								<span className="title-wrapper">
									<span className="title-number">2</span>
									<span className="title-name">BDE Feedback</span>
								</span>
							</h2>

							<span className="h4">
								Product Feedback <span className="required-field">*</span>
							</span>

							<textarea
								name="productFeedback"
								id="product-feedback"
								value={formik.values.productFeedback}
								onChange={(e) => {
									formik.handleChange(e);
									if (formik.errors.productFeedback) {
										formik.setFieldError('productFeedback', '');
									}
								}}
								onBlur={formik.handleBlur}
								required
								className={formik.touched.productFeedback && formik.errors.productFeedback ? 'error' : ''}
							></textarea>
							{formik.touched.productFeedback && formik.errors.productFeedback && (
								<div className="error-message show">{formik.errors.productFeedback}</div>
							)}
						</div>
						<div className="product-feedback-wrapper">
							<span className="h4">
								Any Feedback <span className="required-field">* </span>
							</span>
							<textarea
								name="anyFeedback"
								id="any-feedback"
								value={formik.values.anyFeedback}
								onChange={(e) => {
									formik.handleChange(e);
									if (formik.errors.anyFeedback) {
										formik.setFieldError('anyFeedback', '');
									}
								}}
								onBlur={formik.handleBlur}
								required
								className={formik.touched.anyFeedback && formik.errors.anyFeedback ? 'error' : ''}
							></textarea>
							{formik.touched.anyFeedback && formik.errors.anyFeedback && (
								<div className="error-message show">{formik.errors.anyFeedback}</div>
							)}
						</div>

						{taskListProducts?.data?.feedback_exceptions?.length !== 0 && (
							<FeedbackExpectation {...commonProps} exception={taskListProducts?.data?.feedback_exceptions} />
						)}
						<div className="verify-site-btn">
							<button
								type="button"
								id="verify-site-button"
								aria-label="verify-site-button"
								className="btn btn-secondary btn-small verify-site-button"
								value="verify-site-button"
								onClick={handleVerifyModalOpen}
							>
								Verify Site Visit
							</button>
						</div>
						<Stepper
							nextHandler={handleNext}
							isPending={isVisitFeedbackPending}
							isNext={isNextDisabled}
							totalSteps={totalSteps}
						/>

						{verifyQrModal && (
							<Modal
								modalId="verifyQrModal"
								className="dialog-lg dialog-sm"
								modalOpen={verifyQrModal}
								isEscAllow={false}
								onModalClose={handleVerifyModalClose}
							>
								<VisitCompleteQr siteDetails={siteDetails} verificationKey={QR_KEYS.SITE} />
							</Modal>
						)}
					</>
				</Wrapper>
			</div>
		</section>
	);
};
